import type { TypeRéglage } from '@vigie/shared';

/**
 * Logique d'édition d'une configuration présentée à plat.
 *
 * Le formulaire manipule des chemins aplatis (`database.connection.password`) et des valeurs
 * textuelles, alors que le fichier envoyé au serveur est un objet imbriqué typé. La conversion
 * entre les deux est l'endroit où se cachent les vrais pièges, d'où un module pur et testé plutôt
 * que du code dispersé dans le composant.
 */

/** Marque des clés dont la valeur gagnante vient de l'environnement : lecture seule. */
const SUFFIXE_ENV = ' (env)';

export function estCléEnvironnement(chemin: string): boolean {
  return chemin.endsWith(SUFFIXE_ENV);
}

/**
 * Convertit la saisie textuelle vers le type attendu.
 *
 * `server.port` vaut 2368 en nombre dans le fichier, mais s'affiche « 2368 » dans un champ texte :
 * le renvoyer en chaîne changerait le type, ferait apparaître une modification là où l'utilisateur
 * n'a rien touché, et pourrait être refusé par Ghost. On ne convertit que si le texte se relit
 * dans le même type ; sinon on garde la chaîne, et la validation dira clairement ce qui n'est pas
 * acceptable — mieux qu'un NaN silencieux.
 *
 * Le type **attendu** prime quand on le connaît. Il vient du catalogue des réglages recommandés, et
 * il est indispensable pour une clé encore absente du fichier : sans valeur d'origine dont
 * s'inspirer, `security.staffDeviceVerification` aurait été écrit `"false"` — une chaîne, que
 * JavaScript juge vraie. Le réglage aurait donc fait exactement l'inverse de ce qu'on demandait.
 */
export function valeurTypée(original: unknown, texte: string, type?: TypeRéglage): unknown {
  if (type === 'booleen') return texte === 'true';
  if (type === 'nombre') {
    const nombre = Number(texte);
    return texte.trim() !== '' && Number.isFinite(nombre) ? nombre : texte;
  }
  if (type !== undefined) return texte;

  if (original === undefined || original === null || typeof original === 'string') return texte;
  try {
    const analysé: unknown = JSON.parse(texte);
    return typeof analysé === typeof original ? analysé : texte;
  } catch {
    return texte;
  }
}

/**
 * Écrit une valeur dans une configuration, sans muter l'objet reçu.
 *
 * Les niveaux manquants sont créés au passage : c'est ce qui permet d'ajouter
 * `bulkEmail.mailgun.apiKey` à un fichier qui n'a jamais entendu parler d'infolettres.
 */
export function écrireChemin(
  objet: unknown,
  chemin: string,
  texte: string,
  type?: TypeRéglage,
): unknown {
  return écrire(objet, chemin.split('.'), texte, type);
}

function écrire(
  courant: unknown,
  segments: readonly string[],
  texte: string,
  type?: TypeRéglage,
): unknown {
  const tête = segments[0];
  if (tête === undefined) return texte;

  const base: Record<string, unknown> =
    courant !== null && typeof courant === 'object' && !Array.isArray(courant)
      ? { ...(courant as Record<string, unknown>) }
      : {};

  base[tête] =
    segments.length === 1
      ? valeurTypée(base[tête], texte, type)
      : écrire(base[tête], segments.slice(1), texte, type);
  return base;
}

/**
 * Applique un ensemble de modifications à la configuration en place.
 *
 * `types` porte le type attendu des clés issues du catalogue des réglages recommandés. Il n'y a
 * rien à y mettre pour les clés déjà présentes dans le fichier : leur valeur d'origine suffit à
 * déduire le type.
 */
export function appliquerÉditions(
  base: unknown,
  éditions: Readonly<Record<string, string>>,
  types: Readonly<Record<string, TypeRéglage>> = {},
): unknown {
  let résultat = base;
  for (const [chemin, valeur] of Object.entries(éditions)) {
    // Une clé imposée par l'environnement n'existe pas dans le fichier : l'y écrire créerait une
    // clé parasite qui ne servirait à rien.
    if (estCléEnvironnement(chemin)) continue;
    résultat = écrireChemin(résultat, chemin, valeur, types[chemin]);
  }
  return résultat;
}

/** Groupe d'appartenance d'une clé, pour l'affichage par sections. */
export function groupeDeClé(chemin: string): string {
  const premier = chemin.replace(SUFFIXE_ENV, '').split(/[.]|__/)[0];
  return premier === undefined || premier === '' ? 'divers' : premier;
}

/**
 * Ordre d'affichage des sections : les réglages qu'on vient corriger en premier, puis le reste par
 * ordre alphabétique. Une section inconnue n'est jamais masquée, seulement reléguée.
 */
const ORDRE_PRIVILÉGIÉ = ['url', 'server', 'database', 'mail', 'logging', 'paths', 'privacy'];

export function ordonnerGroupes(groupes: readonly string[]): string[] {
  return [...groupes].sort((a, b) => {
    const rangA = ORDRE_PRIVILÉGIÉ.indexOf(a);
    const rangB = ORDRE_PRIVILÉGIÉ.indexOf(b);
    if (rangA !== -1 && rangB !== -1) return rangA - rangB;
    if (rangA !== -1) return -1;
    if (rangB !== -1) return 1;
    return a.localeCompare(b, 'fr');
  });
}
